import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  CalendarMonth as RentalIcon,
  ShoppingCart as SaleIcon
} from '@mui/icons-material';

/**
 * Modal de choix entre création d'annonce de location et vente de produit
 * Affiche un message clair pour aider l'utilisateur à choisir la bonne option
 */
const CreateChoiceModal = ({ open, onClose }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleRentalChoice = () => {
    onClose();
    navigate('/create-listing');
  };

  const handleSaleChoice = () => {
    onClose();
    navigate('/create-product');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxWidth: 600
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          {t('createChoice.title')}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            mb: 4,
            color: 'text.secondary',
            fontSize: '1rem'
          }}
        >
          {t('createChoice.description')}
        </Typography>

        {/* Option Location/Service - Cliquable */}
        <Box
          onClick={handleRentalChoice}
          sx={{
            mb: 3,
            p: 3,
            border: '2px solid',
            borderColor: 'primary.main',
            borderRadius: 2,
            backgroundColor: 'primary.50',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.shadows[8],
              borderColor: 'primary.dark',
              backgroundColor: 'primary.100'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <RentalIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {t('createChoice.rental.title')}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.primary', lineHeight: 1.6 }}>
            {t('createChoice.rental.description')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            {t('createChoice.rental.examples')}
          </Typography>
        </Box>

        {/* Option Vente - Cliquable */}
        <Box
          onClick={handleSaleChoice}
          sx={{
            mb: 2,
            p: 3,
            border: '2px solid',
            borderColor: 'success.main',
            borderRadius: 2,
            backgroundColor: 'success.50',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.shadows[8],
              borderColor: 'success.dark',
              backgroundColor: 'success.100'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SaleIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
              {t('createChoice.sale.title')}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.primary', lineHeight: 1.6 }}>
            {t('createChoice.sale.description')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            {t('createChoice.sale.examples')}
          </Typography>
        </Box>
      </DialogContent>

      <Box sx={{ textAlign: 'center', pb: 3, pt: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            backgroundColor: 'error.main',
            color: 'white',
            textTransform: 'none',
            fontSize: '0.95rem',
            px: 3,
            py: 1,
            borderRadius: 1,
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: 'error.dark',
              boxShadow: theme.shadows[4]
            },
            transition: 'all 0.3s ease'
          }}
        >
          {t('createChoice.cancel')}
        </Button>
      </Box>
    </Dialog>
  );
};

export default CreateChoiceModal;
